import type { UIMessage } from "ai";

export interface SchemaContextField {
    name?: string;
    dataType?: string;
    description?: string;
    isPrimaryKey?: boolean;
    isSortKey?: boolean;
    isRequired?: boolean;
    isPII?: boolean;
    classifiers?: string[];
}

export interface SchemaContext {
    schemaId?: string;
    name?: string;
    description?: string;
    action?: string;
    fields?: SchemaContextField[];
    createdDate?: string;
    updatedDate?: string;
    createdByEmail?: string;
    updatedByEmail?: string;
    createdByName?: string;
    updatedByName?: string;
}

export interface SchemaListContextItem {
    schemaId?: string;
    name?: string;
    description?: string;
    fieldsCount?: number;
    dataSetCount?: number;
    state?: string;
    totalRecordCount?: number;
    createdDate?: string;
    updatedDate?: string;
    createdByEmail?: string;
    updatedByEmail?: string;
}

export interface SchemaListContext {
    count: number;
    truncated: boolean;
    schemas: SchemaListContextItem[];
}

export function extractSchemaContext(controller: any): SchemaContext | undefined {
    if (!controller) return undefined;
    const schema = controller?.props?.schemaModel || controller?.props?.defaultValue;
    if (!schema) return undefined;
    const fields = Array.isArray(schema?.fields)
        ? schema.fields.slice(0, 200).map((f: any) => ({
              name: f?.name,
              dataType: f?.dataType,
              description: f?.description,
              isPrimaryKey: f?.isPrimaryKey,
              isSortKey: f?.isSortKey,
              isRequired: f?.isRequired,
              isPII: f?.isPII,
              classifiers: Array.isArray(f?.classifiers) ? f.classifiers.map((c: any) => c?.tagName || c).filter(Boolean) : undefined,
          }))
        : undefined;
    return {
        schemaId: schema?.schemaId,
        name: schema?.name,
        description: schema?.description,
        action: controller?.props?.action,
        fields,
        createdDate: schema?.createdDate,
        updatedDate: schema?.updatedDate,
        createdByEmail: schema?.createdByEmail,
        updatedByEmail: schema?.updatedByEmail,
        createdByName: schema?.createdByName,
        updatedByName: schema?.updatedByName,
    };
}

const SCHEMA_LIST_CONTEXT_LIMIT = 100;

export function extractSchemaListContext(schemaList: any[]): SchemaListContext | undefined {
    if (!Array.isArray(schemaList) || schemaList.length === 0) return undefined;
    const schemas = schemaList.slice(0, SCHEMA_LIST_CONTEXT_LIMIT).map((s: any) => ({
        schemaId: s?.schemaId,
        name: s?.name,
        description: s?.description,
        fieldsCount: typeof s?.fieldsCount === "number" ? s.fieldsCount : Array.isArray(s?.fields) ? s.fields.length : undefined,
        dataSetCount: typeof s?.dataSetCount === "number" ? s.dataSetCount : undefined,
        state: s?.state,
        totalRecordCount: typeof s?.totalRecordCount === "number" ? s.totalRecordCount : undefined,
        createdDate: s?.createdDate,
        updatedDate: s?.updatedDate,
        createdByEmail: s?.createdByEmail,
        updatedByEmail: s?.updatedByEmail,
    }));
    return {
        count: schemaList.length,
        truncated: schemaList.length > SCHEMA_LIST_CONTEXT_LIMIT,
        schemas,
    };
}

export function getMessageText(message: UIMessage): string {
    if (!Array.isArray(message.parts)) return "";
    return message.parts
        .filter((p: any) => p?.type === "text")
        .map((p: any) => p.text || "")
        .join("");
}
